<template>
  <div class="consulting-container">
    <div class="crumbs">
      <span class="crumb-link" @click="goBack">造价师详情</span>
      <span> > 咨询</span>
    </div>

    <div class="consulting-wrapper">
      <div ref="dialog" class="dialog">
        <div v-if="loading && conversations.length === 0" class="loading-text">加载中...</div>
        <div v-else-if="conversations.length === 0" class="no-data">暂无对话记录</div>
        <div v-else>
          <!-- 加载更多提示 -->
          <div v-if="hasMore" class="load-more-tip" @click="loadMoreMessages">
            <span v-if="loadingMore">加载中...</span>
            <span v-else>点击查看更多</span>
          </div>
          <div
            v-for="item in conversations"
            :key="item.id"
            class="msg"
            :class="item.sender_type === 2 ? 'left' : 'right'"
          >
            <img
              class="avatar"
              :src="item.sender_type === 1 ? require('@/assets/image/2.jpg') : require('@/assets/image/1.jpg')"
              :alt="item.sender_type_text"
            >
            <div class="bubble" :class="item.sender_type === 1 ? 'primary' : ''">
              <!-- 多行消息内容 - payment_status !== 0 时显示 -->
              <template v-if="item.is_paid !== 1 || item.payment_status !== 0">
                <template v-for="(line, index) in parseMessage(item.message)">
                  <!-- 图片消息 -->
                  <div v-if="isImageMessage(line)" :key="`image-${index}`" class="message-image">
                    <img
                      :src="getImageUrl(line)"
                      alt="图片"
                      class="message-img"
                      @click="previewImage(getImageUrl(line))"
                    >
                  </div>
                  <!-- 文件消息 -->
                  <div v-else-if="isFileMessage(line)" :key="`file-${index}`" class="message-file">
                    <div class="file-info">
                      <img src="@/assets/image/al/file.png" alt="文件" class="file-icon">
                      <span class="file-name">{{ getFileName(line) }}</span>
                    </div>
                    <a
                      :href="getFileUrl(line)"
                      target="_blank"
                      class="file-link"
                      @click.stop
                    >
                      下载文件
                    </a>
                  </div>
                  <!-- 普通文本消息 -->
                  <div v-else-if="line.trim()" :key="`text-${index}`" class="message-text">
                    {{ line }}
                  </div>
                </template>
              </template>
              <div v-if="item.is_paid === 1" class="charge-info">
                <div class="charge-text">针对此问题需要收取<span class="amount">{{ item.amount }}</span>元的费用才能解答。</div>
                <button
                  class="charge-btn"
                  :class="{ 'disabled': item.payment_status !== 0 }"
                  :disabled="item.payment_status !== 0"
                  @click="handlePayReply(item)"
                >
                  {{ item.payment_status === 0 ? '立即解答' : '已解答' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end;">
        <div class="notice">
          本次服务是否为您解决了问题：
          <button class="btn small" @click="showRateModal = true">服务评价</button>
        </div>
      </div>

      <div style="border: 1px solid rgba(17,17,17,0.2);border-radius: 10px;padding: 20px;">
        <div class="upload-buttons">
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="handleImageUpload"
          >
          <input
            ref="fileInput"
            type="file"
            style="display: none"
            @change="handleFileUpload"
          >
          <img
            class="upload-icon"
            src="@/assets/image/al/img.png"
            alt="上传图片"
            @click="triggerImageUpload"
          >
          <img
            class="upload-icon"
            src="@/assets/image/al/file.png"
            alt="上传文件"
            @click="triggerFileUpload"
          >
        </div>
        <div class="editor">
          <textarea
            v-model="newMessage"
            placeholder="请输入您的咨询问题..."
            @keyup.enter="sendMessage"
          />
          <button class="send" :disabled="!newMessage.trim()" @click="sendMessage">发送</button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="showImagePreview" class="image-preview-mask" @click="closeImagePreview">
      <div class="image-preview-container" @click.stop>
        <img :src="previewImageUrl" alt="预览图片" class="preview-image">
        <img
          src="@/assets/image/al/close.png"
          alt="关闭"
          class="preview-close"
          @click="closeImagePreview"
        >
      </div>
    </div>

    <!-- 评价弹窗 -->
    <div v-if="showRateModal" class="modal-mask" @click.self="closeModal">
      <div class="modal">
        <div class="modal-title">评价</div>
        <img class="modal-close" src="@/assets/image/al/close.png" alt="关闭" @click="closeModal">
        <div style="text-align:center;color:#666;">本次服务的满意度评分：</div>
        <div class="stars">
          <img
            v-for="n in 5"
            :key="n"
            class="star-img"
            :src="n <= rating ? require('@/assets/image/scy.png') : require('@/assets/image/scn.png')"
            alt="星"
            @click="setRating(n)"
          >
        </div>
        <textarea v-model="feedback" class="modal-textarea" placeholder="请输入您的意见或建议~" />
        <div class="ta-footer">{{ feedbackCount }}/200</div>
        <button class="primary-btn" @click="submitReview">提交</button>
      </div>
    </div>

    <!-- 支付方式选择弹窗 -->
    <div v-if="showPaymentModal" class="modal-mask" @click.self="closePaymentModal">
      <div class="modal">
        <div class="modal-title">选择支付方式</div>
        <img class="modal-close" src="@/assets/image/al/close.png" alt="关闭" @click="closePaymentModal">
        <div class="payment-options">
          <div
            v-for="method in paymentMethods"
            :key="method.value"
            class="payment-option"
            :class="{ active: selectedPaymentMethod === method.value }"
            @click="selectedPaymentMethod = method.value"
          >
            <div class="payment-icon">{{ method.icon }}</div>
            <div class="payment-label">{{ method.label }}</div>
          </div>
        </div>
        <button class="primary-btn" :disabled="!selectedPaymentMethod || paying" @click="confirmPayment">
          {{ paying ? '支付中...' : '确认支付' }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { createConversation, evaluateEngineer, uploadImage, getConversationList, payPaidReply } from '@/api/user'
import { getUserInfo, checkAuth } from '@/utils/auth'

export default {
  name: 'Consulting',
  data() {
    return {
      showRateModal: false,
      rating: 3,
      feedback: '',
      conversations: [],
      loading: false,
      loadingMore: false,
      newMessage: '',
      engineerId: null,
      submitting: false,
      userInfo: null,
      previewImageUrl: '', // 预览图片URL
      showImagePreview: false, // 是否显示图片预览
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        hasMore: false
      },
      shouldScrollToBottom: false, // 标记是否需要滚动到底部
      showPaymentModal: false, // 支付方式选择弹窗
      selectedPaymentMethod: '', // 选中的支付方式
      currentReplyItem: null, // 当前要支付的回复项
      paymentMethods: [
        { value: 'balance', label: '余额支付', icon: '💰' },
        { value: 'alipay', label: '支付宝', icon: '💳' },
        { value: 'wechat', label: '微信支付', icon: '💳' }
      ],
      paying: false // 支付中状态
    }
  },
  computed: {
    feedbackCount() {
      return this.feedback.length
    },
    isEngineer() {
      return this.userInfo && this.userInfo.group_id === 2
    },
    isUser() {
      return this.userInfo && this.userInfo.group_id === 1
    },
    isSupplier() {
      return this.userInfo && this.userInfo.group_id === 3
    },
    hasMore() {
      return this.pagination.hasMore
    }
  },
  watch: {
    // 监听对话记录变化，自动滚动到底部（仅在发送新消息时）
    conversations: {
      handler() {
        if (this.shouldScrollToBottom) {
          this.$nextTick(() => {
            this.scrollToBottom()
            this.shouldScrollToBottom = false
          })
        }
      },
      deep: true
    }
  },
  created() {
    this.userInfo = getUserInfo()
    this.engineerId = this.$route.query.cost_engineer_id
    // 获取对话记录
    if (this.engineerId) {
      this.fetchConversationList()
    }
  },
  mounted() {
    // 组件挂载后滚动到底部
    this.shouldScrollToBottom = true
  },
  methods: {
    // 获取对话记录列表
    async fetchConversationList() {
      if (!this.engineerId) return

      try {
        if (this.pagination.page === 1) {
          this.loading = true
        } else {
          this.loadingMore = true
        }

        const response = await getConversationList({
          engineer_id: this.engineerId,
          page: this.pagination.page,
          limit: this.pagination.limit
        })

        if (response && response.code === 1 && response.data) {
          const data = response.data
          const newList = data.list || data.messages || []

          if (this.pagination.page === 1) {
            // 首次加载时，替换数据并滚动到底部
            this.conversations = newList
            this.shouldScrollToBottom = true
          } else {
            // 分页加载时，将新数据添加到前面，并保持滚动位置
            const oldScrollHeight = this.$refs.dialog.scrollHeight
            this.conversations = [...newList, ...this.conversations]
            this.$nextTick(() => {
              const newScrollHeight = this.$refs.dialog.scrollHeight
              this.$refs.dialog.scrollTop = newScrollHeight - oldScrollHeight
            })
          }

          // 更新分页信息
          if (data.pagination) {
            this.pagination.total = data.pagination.total || 0
            this.pagination.hasMore = data.pagination.has_more || false
            // 注意：这里不更新 page，因为 page 是请求参数，current_page 是返回的当前页
            // 如果接口返回了 current_page，可以用于同步，但不应该覆盖请求的 page
          } else {
            // 兼容旧的数据结构
            this.pagination.total = data.total || newList.length
            this.pagination.hasMore = this.conversations.length < this.pagination.total
          }
        }
      } catch (error) {
        console.error('获取对话记录失败:', error)
        this.$message && this.$message.error('获取对话记录失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    // 加载更多消息
    async loadMoreMessages() {
      if (this.loadingMore || !this.pagination.hasMore) return

      this.pagination.page++
      await this.fetchConversationList()
    },
    // 滚动到底部
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.dialog
        if (container) {
          // 使用 scrollTo 方法平滑滚动到底部
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          })
        }
      })
    },
    async sendMessage() {
      // 检查登录状态
      if (!checkAuth(this.$router, '请先登录后再发送消息')) {
        return
      }
      const message = this.newMessage.trim()
      if (!message) {
        this.$message && this.$message.warning('请输入消息内容')
        return
      }

      if (!this.engineerId) {
        this.$message && this.$message.warning('造价师ID不存在')
        return
      }

      try {
        const data = {
          engineer_id: this.engineerId,
          message: message, // 咨询内容
          sender_type: this.userInfo.group_id // 根据用户身份设置：1=用户，2=造价师
        }

        // 仅造价师发送时可传付费相关字段
        if (Number(this.userInfo.group_id) === 2) {
          data.is_paid = 0
          data.amount = '0.00'
        }

        await createConversation(data)
        this.newMessage = ''
        this.$message && this.$message.success('发送成功')
        // 刷新对话记录（重置分页）
        this.pagination.page = 1
        this.pagination.hasMore = true
        this.shouldScrollToBottom = true
        await this.fetchConversationList()
      } catch (error) {
        console.error('发送消息失败:', error)
        this.$message && this.$message.error('发送失败，请重试')
      }
    },
    // 触发图片上传
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    // 触发文件上传
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    // 处理图片上传
    async handleImageUpload(event) {
      // 检查登录状态
      if (!checkAuth(this.$router, '请先登录后再上传图片')) {
        return
      }
      const file = event.target.files[0]
      if (!file) return

      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        this.$message && this.$message.error('请选择图片文件')
        event.target.value = ''
        return
      }

      // 检查文件大小（限制为10MB）
      if (file.size > 10 * 1024 * 1024) {
        this.$message && this.$message.error('图片大小不能超过10MB')
        event.target.value = ''
        return
      }

      try {
        this.$message && this.$message.info('正在上传图片...')
        const response = await uploadImage(file)
        if (response && response.code === 1 && response.data) {
          // 保存输入框原始内容
          const originalMessage = this.newMessage
          // 获取图片URL
          const imageUrl = response.data.fullurl || response.data.url
          // 设置消息内容并发送
          this.newMessage = `[图片]${imageUrl}`
          await this.sendMessage()
          // 恢复输入框原始内容
          this.newMessage = originalMessage
        } else {
          this.$message && this.$message.error(response?.msg || '图片上传失败')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message && this.$message.error('图片上传失败，请重试')
      } finally {
        // 清空input值，以便可以重复选择同一文件
        const input = event.target
        if (input) {
          input.value = ''
        }
      }
    },
    // 处理文件上传
    async handleFileUpload(event) {
      // 检查登录状态
      if (!checkAuth(this.$router, '请先登录后再上传文件')) {
        return
      }
      const file = event.target.files[0]
      if (!file) return

      // 检查文件大小（限制为50MB）
      if (file.size > 50 * 1024 * 1024) {
        this.$message && this.$message.error('文件大小不能超过50MB')
        event.target.value = ''
        return
      }

      try {
        this.$message && this.$message.info('正在上传文件...')
        const response = await uploadImage(file)
        if (response && response.code === 1 && response.data) {
          // 保存输入框原始内容
          const originalMessage = this.newMessage
          // 获取文件URL
          const fileUrl = response.data.fullurl || response.data.url
          // 设置消息内容并发送
          this.newMessage = `[文件]${file.name} ${fileUrl}`
          await this.sendMessage()
          // 恢复输入框原始内容
          this.newMessage = originalMessage
        } else {
          this.$message && this.$message.error(response?.msg || '文件上传失败')
        }
      } catch (error) {
        console.error('文件上传失败:', error)
        this.$message && this.$message.error('文件上传失败，请重试')
      } finally {
        // 清空input值，以便可以重复选择同一文件
        const input = event.target
        if (input) {
          input.value = ''
        }
      }
    },
    setRating(value) {
      this.rating = value
    },
    closeModal() {
      this.showRateModal = false
    },
    async submitReview() {
      // 检查登录状态
      if (!checkAuth(this.$router, '请先登录后再进行评价')) {
        return
      }
      if (this.submitting) return

      try {
        this.submitting = true
        // 使用最新的对话记录ID，如果没有则使用第一个
        const conversationId = this.conversations.length > 0 ? this.conversations[0].id : null

        if (!conversationId) {
          this.$message && this.$message.warning('暂无对话记录，无法评价')
          return
        }

        const data = {
          conversation_id: conversationId,
          rating: this.rating,
          comment: this.feedback.trim()
        }

        await evaluateEngineer(data)
        this.showRateModal = false
        this.feedback = ''
        this.rating = 3
        this.$message && this.$message.success('感谢您的评价！')
      } finally {
        this.submitting = false
      }
    },
    goBack() {
      this.$router.back()
    },
    // 解析消息，按换行符分割
    parseMessage(message) {
      if (!message) return []
      // 将 \n 转换为实际换行，然后分割
      return message.split(/\n/).filter(line => line.trim())
    },
    // 判断是否是图片消息
    isImageMessage(message) {
      if (!message) return false
      // 支持 [图片] 前缀格式
      if (message.startsWith('[图片]')) return true
      // 支持直接是图片路径（通过扩展名判断）
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const trimmed = message.trim()
      return imageExtensions.some(ext => trimmed.toLowerCase().endsWith(ext))
    },
    // 判断是否是文件消息
    isFileMessage(message) {
      return message && message.startsWith('[文件]')
    },
    // 获取图片URL
    getImageUrl(message) {
      if (this.isImageMessage(message)) {
        let url = message.trim()
        // 如果有 [图片] 前缀，移除它
        if (url.startsWith('[图片]')) {
          url = url.replace('[图片]', '')
        }
        // 如果URL不是完整路径，添加基础路径
        if (url && !url.startsWith('http')) {
          return this.$getImageUrl ? this.$getImageUrl(url) : url
        }
        return url
      }
      return ''
    },
    // 获取文件URL
    getFileUrl(message) {
      if (this.isFileMessage(message)) {
        const parts = message.split(' ')
        if (parts.length > 1) {
          const url = parts[parts.length - 1]
          // 如果URL不是完整路径，添加基础路径
          if (url && !url.startsWith('http')) {
            return this.$getImageUrl ? this.$getImageUrl(url) : url
          }
          return url
        }
      }
      return ''
    },
    // 获取文件名
    getFileName(message) {
      if (this.isFileMessage(message)) {
        const parts = message.split(' ')
        if (parts.length > 1) {
          return parts[0].replace('[文件]', '')
        }
      }
      return '未知文件'
    },
    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url
      this.showImagePreview = true
    },
    // 关闭图片预览
    closeImagePreview() {
      this.showImagePreview = false
      this.previewImageUrl = ''
    },
    // 处理支付回复
    handlePayReply(item) {
      if (item.payment_status !== 0) {
        return // 已支付，不处理
      }
      this.currentReplyItem = item
      this.selectedPaymentMethod = ''
      this.showPaymentModal = true
    },
    // 关闭支付弹窗
    closePaymentModal() {
      this.showPaymentModal = false
      this.selectedPaymentMethod = ''
      this.currentReplyItem = null
    },
    // 确认支付
    async confirmPayment() {
      if (!this.selectedPaymentMethod || !this.currentReplyItem) {
        this.$message && this.$message.warning('请选择支付方式')
        return
      }

      this.paying = true
      try {
        const response = await payPaidReply({
          reply_id: this.currentReplyItem.id,
          payment_method: this.selectedPaymentMethod
        })

        if (response && response.code === 1) {
          this.$message && this.$message.success('支付成功')
          this.closePaymentModal()
          // 刷新对话记录
          await this.fetchConversationList()
        } else {
          this.$message && this.$message.error(response?.msg || '支付失败')
        }
      } catch (error) {
        console.error('支付失败:', error)
        this.$message && this.$message.error('支付失败，请重试')
      } finally {
        this.paying = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.consulting-container {
  width: 90%;
  max-width: 1400px;
  margin: 20px auto;
  background: #fff;
  min-height: calc(100vh - var(--navbar-total-height, 80px) - 40px);
  min-height: calc(100dvh - var(--navbar-total-height, 80px) - 40px);
  padding: 20px;
}

.consulting-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.crumbs {
  color: #999;
  margin-bottom: 12px;
}
.crumb-link { cursor: pointer; color:#0F70E2; }
.dialog {
  background: #fff;
  padding: 20px;
  border: 1px solid rgba(17,17,17,0.2);
  border-radius: 10px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
}
.msg {
  display: flex;
  align-items: flex-start;
  margin-bottom: 14px;
}
.msg.right {
  flex-direction: row-reverse;
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin: 0 8px;
}
.bubble {
  background: #EEF4FB;
  padding: 10px 12px;
  border-radius: 8px;
  max-width: 70%;
}
.bubble.primary {
  background: #0f70e2;
  color: #fff;
}
.notice {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  margin: 16px 0;
  border: 1px solid rgba(17,17,17,0.2);
  border-radius: 100px;
  padding: 16px 20px;
}
.notice .icon {
  width: 18px;
  height: 18px;
}
.btn.small {
  padding: 4px 10px;
  border: none;
  background: #0f70e2;
  color: #fff;
  border-radius: 100px;
  cursor: pointer;
}
.editor {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}
.editor textarea {
  flex: 1;
  height: 100px;
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  padding: 10px;
  resize: vertical;
}
.editor textarea:focus {
  border-color: #e5e6eb;
  outline: none;
  box-shadow: none;
}
.send {
  width: 120px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #0f70e2;
  color: #fff;
  cursor: pointer;
}

/* 评价弹窗 */
.modal-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  position: relative;
  width: 480px;
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px 24px;
}
.modal-title { text-align:center; font-weight:600; margin-bottom: 12px; }
.modal-close { position:absolute; right:12px; top:12px; width:20px; height:20px; cursor:pointer; }
.stars { display:flex; justify-content:center; gap:8px; margin: 8px 0 12px; }
.star-img { width:22px; height:22px; cursor:pointer; }
.modal-textarea { width:100%; height:120px; border:1px solid #e5e6eb; border-radius:6px; padding:10px; resize: vertical; }
.modal-textarea:focus { border-color:#e5e6eb; outline:none; box-shadow:none; }
.ta-footer { text-align:right; color:#999; font-size:12px; margin-top:6px; }
.primary-btn { width:100%; height:44px; border:none; border-radius:6px; color:#fff; background: linear-gradient(90deg, #0F6FE2, #0C89F2); cursor:pointer; margin-top: 14px; }

.loading-text, .no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

.load-more-tip {
  text-align: center;
  color: #0f70e2;
  padding: 12px;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  transition: color 0.3s ease;
  border-bottom: 1px solid rgba(17, 17, 17, 0.1);
  margin-bottom: 10px;
}

.load-more-tip:hover {
  color: #0c5bb8;
}

.load-more-tip:active {
  opacity: 0.7;
}

.charge-info {
  margin-top: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.charge-text {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  word-wrap: break-word;
  word-break: break-all;
}

.charge-text .amount {
  color: #ff4757;
  font-weight: bold;
}

.charge-btn {
  background: #0f70e2;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  align-self: flex-end;
  white-space: nowrap;
  flex-shrink: 0;
}

.charge-btn:hover {
  background: #0c5bb8;
}

.charge-btn.disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.charge-btn.disabled:hover {
  background: #ccc;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid #e5e6eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-option:hover {
  border-color: #0f70e2;
  background: #f0f7ff;
}

.payment-option.active {
  border-color: #0f70e2;
  background: #f0f7ff;
}

.payment-icon {
  font-size: 24px;
}

.payment-label {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.primary-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.send:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.upload-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.upload-icon {
  width: 30px;
  height: 30px;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
}

// 消息内容样式
.message-text {
  word-wrap: break-word;
  word-break: break-all;
}

.message-image {
  margin-bottom: 8px;

  .message-img {
    max-width: 300px;
    max-height: 300px;
    border-radius: 8px;
    cursor: pointer;
    display: block;
    object-fit: contain;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
}

.message-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  margin-bottom: 8px;

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;

    .file-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .file-name {
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .file-link {
    color: #0f70e2;
    text-decoration: underline;
    font-size: 14px;
    margin-left: 12px;
    flex-shrink: 0;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
      color: #0c5bb8;
    }
  }
}

.bubble.primary .message-file {
  background: rgba(255, 255, 255, 0.15);

  .file-link {
    color: #fff;
    background: rgba(255, 255, 255, 0.2);
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      color: #fff;
    }
  }
}

// 图片预览弹窗
.image-preview-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.preview-close {
  position: absolute;
  right: -40px;
  top: 0;
  width: 30px;
  height: 30px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 5px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
}

@media (max-width: 1600px) {
  .consulting-container {
    width: 94%;
    max-width: none;
    margin: 12px auto;
    padding: 16px;
  }
}

@media (max-width: 1366px) {
  .consulting-container {
    width: 98%;
    padding: 12px;
  }
}

@media (max-height: 800px) {
  .consulting-container {
    min-height: auto;
  }
}
</style>
